// src/store/planStore.ts
/**
 * .tuMetrics {"tu1": 0.81, "tu2": 0.86, "tu3": 7, "notes": "Plan store realtime setup now avoids duplicate first-load fetch while already loading", "tuVersion": "2.5"}
 * .help "Plan state store for loading, filtering, realtime sync, and lifecycle actions."
 */
import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import useUserStore from './userStore';
import useTeamStore from './teamStore';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { diffMarkdown } from '@/features/documents/markdown/diff';
import { groupPlanFamilies } from '@/features/plans/planFamily';

export { groupPlanFamilies, versionRank, pickParamountVersion, editTargetForFamily } from '@/features/plans/planFamily';

// Types for Living Plans

export interface Plan {
  id: string;
  name: string;
  description?: string;
  status: 'draft' | 'active' | 'completed' | 'archived';
  version: string; // Legacy: "0.0.1", "1.0.0" - kept for backward compatibility
  team_id?: string;
  company_id: string;
  created_by: string;
  participants: string[];
  execution_status: 'not_executed' | 'partial' | 'completed';
  progress_percentage: number;
  /** Canonical plan text (GFM markdown). See docs/plans/DOCUMENTS_MARKDOWN_PLANS_GUIDES.md. */
  body_markdown: string;
  /** Legacy tree JSON from the retired tree editor. Not written any more; dropped in a later cleanup. */
  plan_content?: unknown;
  plan_settings: any; // Plan-specific settings
  related_entity_type?: string;
  related_entity_id?: string;
  ticketNumber?: number; // Sequential number within project (e.g., 5 in CLINIC-P5)
  tags: string[];
  is_template: boolean;
  template_category?: string;
  estimated_duration_days?: number;
  actual_duration_days?: number;
  priority: 'low' | 'medium' | 'high' | 'critical';
  editing_by?: string; // User ID currently editing (for edit locking)
  editing_since?: string; // Timestamp when edit lock was acquired
  created_at: string;
  updated_at: string;
  
  // ============================================
  // NEW: LIVING PLANS VERSIONING SYSTEM
  // ============================================
  // These fields enable true version management:
  // - Draft versions: 0.0.1 → 0.0.2 → 0.0.3 (iterating on the plan)
  // - Execute: 0.0.X → 1.0.0 (plan goes live, becomes read-only)
  // - Next versions: 1.0 → 1.1 → 1.2 or 1.X → 2.0 (future iterations)
  // Each plan record = 1 version. Versions linked via parent_version_id.
  // ============================================
  major_version?: number; // 0 = draft, 1+ = executed (e.g., v1.0.0, v2.0.0)
  minor_version?: number; // Dot releases (e.g., v1.1, v1.2)
  patch_version?: number; // Auto-incremented on content changes (e.g., v1.0.1, v1.0.2)
  version_state?: 'draft' | 'executing' | 'complete'; // Execution state (separate from status)
  parent_version_id?: string; // Links to parent version (v1.1 → v1.0, v2.0 → v1.X)
  /** Newest by version number (planning ahead). */
  latest_version?: Plan;
  /** List face: executing > complete > newest draft. */
  paramount_version?: Plan;
  executed_at?: string; // Timestamp when "Execute Plan" clicked (draft → executing)
  completed_at?: string; // Timestamp when plan finished (executing → complete)
}

export interface PlanItem {
  id: string;
  plan_id: string;
  item_type: 'task' | 'subtask' | 'requirement' | 'sprint' | 'idea' | 'milestone' | 'note' | 'section';
  title: string;
  description?: string;
  content: any; // Rich content for the item
  properties: any; // Type-specific properties
  parent_id?: string;
  order_index: number;
  version: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  assignee_id?: string;
  due_date?: string;
  estimated_hours?: number;
  actual_hours?: number;
  complexity?: number;
  tags: string[];
  is_executed: boolean;
  execution_notes?: string;
  created_at: string;
  updated_at: string;
}

export interface PlanExecution {
  id: string;
  plan_id: string;
  executed_by: string;
  execution_type: 'full' | 'partial' | 'incremental';
  execution_summary: any;
  created_cards: string[];
  created_sprints: string[];
  created_requirements: string[];
  created_roadmaps: string[];
  execution_status: 'in_progress' | 'completed' | 'failed' | 'cancelled';
  error_message?: string;
  execution_log: any[];
  created_at: string;
}

export interface PlanTemplate {
  id: string;
  name: string;
  description?: string;
  category: string;
  template_content: any;
  template_items: any;
  tags: string[];
  is_public: boolean;
  company_id?: string;
  created_by: string;
  usage_count: number;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
}

// ============================================
// PERFORMANCE OPTIMIZATION: Pagination
// ============================================
// Strategy: Load 50 plans initially, with Load More functionality.
// ============================================
const PLANS_PER_PAGE = 50;

// ============================================
// LEGACY: Version Snapshots (Historical)
// ============================================
// This is the OLD version snapshot system (now called plan_version_snapshots in DB).
// Kept for backward compatibility and historical audit trail.
// NEW system uses Plan records directly as versions (see Plan.parent_version_id).
// ============================================
export interface PlanVersion {
  id: string;
  plan_id: string;
  version_number: string;
  created_by: string;
  change_summary: any; // JSONB with structured change data
  body_markdown: string | null; // markdown snapshot (null for pre-markdown snapshots)
  plan_content: unknown; // legacy tree snapshot
  plan_items: any; // JSONB snapshot
  is_current: boolean;
  created_at: string;
}

export interface PlanViewer {
  plan_id: string;
  user_id: string;
  viewing_since: string;
}

// ============================================
// NEW: Version Hierarchy (Living Plans Versioning)
// ============================================
// Represents the version tree for a plan family.
// Example: v0.0.042 (draft) → v1.0.0 (executing) → v1.1.0 (draft) → v2.0.0 (draft)
// Used by version drawer to show all versions and enable switching.
// ============================================
export interface PlanVersionHierarchy {
  id: string;
  ticket_number: number;
  name: string;
  major_version: number;
  minor_version: number;
  patch_version: number;
  version: string; // Formatted: "0.0.1", "1.0.0", etc.
  version_state: 'draft' | 'executing' | 'complete';
  parent_version_id: string | null;
  executed_at: string | null;
  completed_at: string | null;
  project_id: string;
  company_id: string;
  created_by: string;
  created_at: string;
  depth: number; // Tree depth (0 = root, 1 = child, etc.)
  path: string[]; // Array of version IDs from root to this version
  version_path: string[]; // Array of version strings (e.g., ["0.0.42", "1.0.0", "1.1.0"])
  project_key: string | null;
  full_ticket_id: string | null; // e.g., "CLINIC-P1"
  child_version_count: number; // How many child versions exist
  progress_percentage: number; // 0-100 (0 for draft, calculated for executing, 100 for complete)
}

// ============================================
// NEW: Drift Tracking
// ============================================
// Drift = tasks added during plan execution that weren't in the original plan.
// Used to track scope creep, unplanned bugs, elevated priorities, etc.
// Allows categorization for analytics and auto-sync to next version.
// ============================================
export interface DriftCandidate {
  card_id: string;
  card_title: string;
  added_at: string;
  added_by: string;
  is_categorized: boolean;
  drift_type: 'unplanned_bug' | 'scope_creep' | 'elevated_priority' | 'next_dot_rev' | null;
}

export type DriftType = 'unplanned_bug' | 'scope_creep' | 'elevated_priority' | 'next_dot_rev';

interface PlanState {
  // State
  plans: Plan[];
  planItems: PlanItem[];
  planExecutions: PlanExecution[];
  planTemplates: PlanTemplate[];
  planVersions: PlanVersion[]; // Version history for selected plan
  planViewers: PlanViewer[]; // Current viewers for selected plan
  selectedPlan: Plan | null;
  selectedPlanItems: PlanItem[];
  isLoading: boolean;
  error: string | null;
  lastFetchedAt: number | null; // Timestamp of last fetch for staleness check
  realtimeChannel: RealtimeChannel | null;
  presenceChannel: RealtimeChannel | null; // Separate channel for presence tracking
  hasMorePlans: boolean; // PERFORMANCE: Track if more plans available
  isLoadingMore: boolean; // PERFORMANCE: Track loading more state
  
  // Filters
  searchTerm: string;
  statusFilter: string;
  teamFilter: string | null;
  
  // Actions
  fetchPlans: (companyId: string, teamId?: string, options?: { limit?: number; offset?: number }) => Promise<void>;
  loadMorePlans: () => Promise<void>; // PERFORMANCE: Load more plans
  refreshPlansIfStale: () => Promise<void>;
  setupRealtimeSubscription: () => Promise<void>;
  cleanupRealtimeSubscription: () => void;
  fetchPlan: (planId: string) => Promise<void>;
  fetchPlanItems: (planId: string) => Promise<void>;
  fetchPlanTemplates: (companyId: string) => Promise<void>;
  fetchPlanVersions: (planId: string) => Promise<void>;
  
  createPlan: (plan: Omit<Plan, 'id' | 'created_at' | 'updated_at'>) => Promise<Plan>;
  updatePlan: (planId: string, updates: Partial<Plan>, createVersionSnapshot?: boolean) => Promise<void>;
  deletePlan: (planId: string) => Promise<void>;
  
  createPlanItem: (item: Omit<PlanItem, 'id' | 'created_at' | 'updated_at'>) => Promise<PlanItem>;
  updatePlanItem: (itemId: string, updates: Partial<PlanItem>) => Promise<void>;
  deletePlanItem: (itemId: string) => Promise<void>;
  
  executePlan: (planId: string, executionType: 'full' | 'partial' | 'incremental') => Promise<PlanExecution>;
  duplicatePlan: (planId: string, newName: string) => Promise<Plan>;
  
  createPlanFromTemplate: (templateId: string, planName: string, teamId?: string) => Promise<Plan>;
  
  // Edit locking
  acquireEditLock: (planId: string) => Promise<boolean>;
  releaseEditLock: (planId: string) => Promise<void>;
  checkEditLock: (planId: string) => Promise<{ locked: boolean; editingBy?: string; editingSince?: string }>;
  
  // Presence tracking
  addViewer: (planId: string) => Promise<void>;
  removeViewer: (planId: string) => Promise<void>;
  fetchViewers: (planId: string) => Promise<void>;
  setupPresenceSubscription: (planId: string) => Promise<void>;
  cleanupPresenceSubscription: () => void;
  
  // Version snapshots (legacy)
  createVersionSnapshot: (planId: string, changeSummary: any) => Promise<void>;
  
  // ============================================
  // NEW: Living Plans Versioning Actions
  // ============================================
  // These actions enable the full versioning lifecycle:
  // 1. fetchPlanVersionHierarchy() - Get all versions of a plan (for version drawer)
  // 2. createNextPlanVersion() - Spawn v1.1 from v1.0 or v2.0 from v1.X
  // 3. executeActivePlan() - Transition draft → executing (0.0.X → 1.0.0)
  // 4. completePlan() - Mark executing plan as complete
  // 5. detectPlanDrift() - Find tasks added during execution (drift candidates)
  // 6. categorizeDrift() - Categorize drift item (bug, scope creep, etc.)
  // ============================================
  
  /**
   * Fetches the complete version hierarchy for a plan family.
   * Used by version drawer to show all versions (v0.0.X, v1.0, v1.1, v2.0, etc.)
   * @param ticketNumber - Plan's permanent ticket number (e.g., 1 in CLINIC-P1)
   * @param projectId - Project UUID
   * @returns Array of all versions in hierarchy order
   */
  fetchPlanVersionHierarchy: (rootPlanId: string) => Promise<PlanVersionHierarchy[]>;
  
  /**
   * Creates the next version of a plan (dot or major release).
   * - Dot release: v1.0 → v1.1 (incremental improvement)
   * - Major release: v1.X → v2.0 (significant rewrite)
   * Automatically copies content and items, syncs "Next Dot Rev" drift.
   * @param sourcePlanId - ID of the plan to version from (must be executing or complete)
   * @param versionType - 'dot' for v1.1, 'major' for v2.0
   * @returns The newly created plan version
   */
  createNextPlanVersion: (sourcePlanId: string, versionType: 'dot' | 'major') => Promise<Plan>;
  
  /**
   * Executes a draft plan (transitions 0.0.X → 1.0.0).
   * - Locks the plan (read-only)
   * - Creates work items (cards, sprints, features)
   * - Starts tracking drift
   * @param planId - Draft plan to execute
   * @returns Execution record with created items
   */
  executeActivePlan: (planId: string) => Promise<PlanExecution>;
  
  /**
   * Marks an executing plan as complete (executing → complete state).
   * Locks the version permanently and calculates final metrics.
   * @param planId - Executing plan to complete
   */
  completePlan: (planId: string) => Promise<void>;
  
  /**
   * Detects tasks added during plan execution (drift candidates).
   * These are tasks created AFTER execute_at timestamp.
   * Used to populate drift tracking UI and analytics.
   * @param planId - Executing plan to check for drift
   * @returns Array of drift candidates (categorized and uncategorized)
   */
  detectPlanDrift: (planId: string) => Promise<DriftCandidate[]>;
  
  /**
   * Categorizes a drift item (task added during execution).
   * Types:
   * - unplanned_bug: Bug discovered during execution
   * - scope_creep: New feature added mid-execution
   * - elevated_priority: Backlog item now urgent
   * - next_dot_rev: Important but can wait for v1.1
   * @param cardId - Task to categorize
   * @param driftType - Category
   * @param reason - Why was this added? (required comment)
   */
  categorizeDrift: (cardId: string, driftType: DriftType, reason: string) => Promise<void>;
  
  /**
   * Loads a plan version snapshot from plan_version_snapshots table.
   * Used to restore and view historical plan versions.
   */
  loadPlanVersionSnapshot: (planId: string) => Promise<{ body_markdown: string | null; plan_items: any; version_number: string; created_at: string } | null>;
  
  setSelectedPlan: (plan: Plan | null) => void;
  setSearchTerm: (term: string) => void;
  setStatusFilter: (status: string) => void;
  setTeamFilter: (teamId: string | null) => void;
  
  clearError: () => void;
}

const usePlanStore = create<PlanState>((set, get) => ({
  // Initial state
  plans: [],
  planItems: [],
  planExecutions: [],
  planTemplates: [],
  planVersions: [],
  planViewers: [],
  selectedPlan: null,
  selectedPlanItems: [],
  isLoading: false,
  hasMorePlans: false,
  isLoadingMore: false,
  error: null,
  lastFetchedAt: null,
  realtimeChannel: null,
  presenceChannel: null,
  searchTerm: '',
  statusFilter: 'all',
  teamFilter: null,

  // Fetch all plans for a company
  fetchPlans: async (companyId: string, teamId?: string, options?: { limit?: number; offset?: number }) => {
    set({ isLoading: true, error: null });
    
    try {
      let query = supabase
        .from('plans')
        .select('*')
        .eq('company_id', companyId)
        .order('updated_at', { ascending: false });

      // PERFORMANCE: Apply pagination if provided
      if (options?.limit) {
        query = query.limit(options.limit);
      }
      if (options?.offset) {
        query = query.range(options.offset, (options.offset + (options.limit || 0)) - 1);
      }

      // Optional teamId is a real home/private team only (never Company-as-all).
      if (teamId) {
        const { teams } = useTeamStore.getState();
        const selectedTeam = teams.find(t => t.id === teamId);
        if (selectedTeam && selectedTeam.name !== 'Company') {
          query = query.eq('team_id', teamId);
        }
      }

      const { data, error } = await query;

      if (error) throw error;

      console.log('fetchPlans result:', { companyId, teamId, dataCount: data?.length, data });
      let plansData = data || [];
      
      // One card per family: latest_version (newest) + paramount_version (list face). Children not listed.
      plansData = groupPlanFamilies(plansData as Plan[]);
      
      // Check if there are more plans (if we got exactly PER_PAGE, there might be more)
      set({ 
        plans: plansData, 
        isLoading: false, 
        lastFetchedAt: Date.now(),
        hasMorePlans: plansData.length === (options?.limit || PLANS_PER_PAGE)
      });
    } catch (error) {
      console.error('Error fetching plans:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to fetch plans', isLoading: false, hasMorePlans: false });
    }
  },

  // Load more plans (pagination)
  loadMorePlans: async () => {
    const { plans, isLoadingMore, teamFilter } = get();
    if (isLoadingMore) return;

    set({ isLoadingMore: true });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser?.companyId) {
        set({ isLoadingMore: false });
        return;
      }

      // Fetch next page
      let query = supabase
        .from('plans')
        .select('*')
        .eq('company_id', currentUser.companyId)
        .order('updated_at', { ascending: false })
        .range(plans.length, plans.length + PLANS_PER_PAGE - 1);

      // Apply team filter if set
      if (teamFilter) {
        const { teams } = useTeamStore.getState();
        const selectedTeam = teams.find(t => t.id === teamFilter);
        const teamIdForQuery = selectedTeam?.name === 'Company' ? null : teamFilter; // Company never used as filter
        if (teamIdForQuery !== null) {
          query = query.eq('team_id', teamIdForQuery);
        }
      }

      const { data, error } = await query;
      if (error) throw error;

      const newPlans = data || [];
      set({
        plans: [...plans, ...newPlans],
        hasMorePlans: newPlans.length === PLANS_PER_PAGE,
        isLoadingMore: false
      });
    } catch (error: any) {
      console.error('Error loading more plans:', error);
      set({ error: error.message || 'Failed to load more plans', isLoadingMore: false });
    }
  },

  refreshPlansIfStale: async () => {
    const { lastFetchedAt, isLoading, plans } = get();
    const now = Date.now();
    const STALE_THRESHOLD = 30000; // 30 seconds

    // If no plans loaded yet, fetch
    if (plans.length === 0) {
      const currentUser = useUserStore.getState().currentUser;
      if (currentUser?.companyId) {
        await get().fetchPlans(currentUser.companyId, get().teamFilter || undefined, { limit: PLANS_PER_PAGE });
      }
      return;
    }

    // Skip if already loading or data is fresh
    if (isLoading || (lastFetchedAt && now - lastFetchedAt < STALE_THRESHOLD)) {
      console.log('refreshPlansIfStale: Data is fresh, skipping refresh');
      return;
    }

    console.log('refreshPlansIfStale: Data is stale, refreshing...');
    const currentUser = useUserStore.getState().currentUser;
    if (currentUser?.companyId) {
      await get().fetchPlans(currentUser.companyId, get().teamFilter || undefined);
    }
  },

  /**
   * Setup realtime subscription for plans
   */
  setupRealtimeSubscription: async () => {
    // Clean up existing subscription
    get().cleanupRealtimeSubscription();

    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser?.companyId) {
      console.warn('setupRealtimeSubscription: No current user companyId found. Skipping realtime subscription setup.');
      return;
    }

    // Ensure plans are loaded first
    const { plans, isLoading } = get();
    if (plans.length === 0 && !isLoading) {
      await get().fetchPlans(currentUser.companyId, get().teamFilter || undefined);
    }

    const channel = supabase
      .channel('plans_channel')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'plans',
          filter: `company_id=eq.${currentUser.companyId}`,
        },
        (payload) => {
          console.log('Real-time plan payload received:', payload);
          const { eventType, new: newRow, old: oldRow } = payload;

          switch (eventType) {
            case 'INSERT': {
              const newPlan = newRow as Plan;
              // Dedupe: streamStore's agent-object handler may have inserted it already
              set((state) =>
                state.plans.some((p) => p.id === newPlan.id)
                  ? state
                  : { plans: [newPlan, ...state.plans] }
              );
              break;
            }

            case 'UPDATE': {
              const updatedPlan = newRow as Plan;
              set((state) => ({
                plans: state.plans.map((p) =>
                  p.id === updatedPlan.id ? updatedPlan : p
                ),
                selectedPlan:
                  state.selectedPlan?.id === updatedPlan.id ? updatedPlan : state.selectedPlan,
              }));
              break;
            }

            case 'DELETE': {
              set((state) => ({
                plans: state.plans.filter((p) => p.id !== oldRow.id),
                selectedPlan:
                  state.selectedPlan?.id === oldRow.id ? null : state.selectedPlan,
              }));
              break;
            }
          }
        }
      )
      .subscribe();

    set({ realtimeChannel: channel });
    console.log('Plans realtime subscription established');
  },

  /**
   * Cleanup realtime subscription
   */
  cleanupRealtimeSubscription: () => {
    const { realtimeChannel } = get();
    if (realtimeChannel) {
      supabase.removeChannel(realtimeChannel);
      set({ realtimeChannel: null });
      console.log('Plans realtime subscription cleaned up');
    }
  },

  // Fetch a specific plan
  fetchPlan: async (planId: string) => {
    set({ isLoading: true, error: null });
    
    try {
      const { data, error } = await supabase
        .from('plans')
        .select('*')
        .eq('id', planId)
        .single();

      if (error) throw error;

      set({ selectedPlan: data, isLoading: false });
    } catch (error) {
      console.error('Error fetching plan:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to fetch plan', isLoading: false });
    }
  },

  // Fetch plan items for a specific plan
  fetchPlanItems: async (planId: string) => {
    try {
      const { data, error } = await supabase
        .from('plan_items')
        .select('*')
        .eq('plan_id', planId)
        .order('order_index', { ascending: true });

      if (error) throw error;

      set({ selectedPlanItems: data || [] });
    } catch (error) {
      console.error('Error fetching plan items:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to fetch plan items' });
    }
  },

  // Fetch plan templates
  fetchPlanTemplates: async (companyId: string) => {
    try {
      const { data, error } = await supabase
        .from('plan_templates')
        .select('*')
        .or(`company_id.eq.${companyId},is_public.eq.true`)
        .order('usage_count', { ascending: false });

      if (error) throw error;

      set({ planTemplates: data || [] });
    } catch (error) {
      console.error('Error fetching plan templates:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to fetch plan templates' });
    }
  },

  // Create a new plan
  createPlan: async (planData: Omit<Plan, 'id' | 'created_at' | 'updated_at'>) => {
    set({ isLoading: true, error: null });
    
    try {
      // Prevent duplicate creation by checking if a plan with same name was just created
      const existingPlan = get().plans.find(
        p => p.name === planData.name && 
        p.company_id === planData.company_id &&
        Date.now() - new Date(p.created_at).getTime() < 5000 // Within last 5 seconds
      );
      
      if (existingPlan) {
        console.warn('Duplicate plan creation prevented:', existingPlan.id);
        set({ isLoading: false });
        return existingPlan;
      }
      
      const { data, error } = await supabase
        .from('plans')
        .insert([planData])
        .select()
        .single();

      if (error) throw error;

      console.log('Plan created:', data);
      
      // Create feed event for plan creation (non-critical)
      try {
        const { default: useFeedStore } = await import('./feedStore');
        const currentUser = useUserStore.getState().currentUser;
        
        useFeedStore.getState().addEvent({
          type: 'plan_created',
          title: 'Plan Created',
          description: `${currentUser?.name || 'User'} created plan "${data.name}"`,
          userId: currentUser?.id || data.created_by,
          initiatorId: currentUser?.id || data.created_by,
          planId: data.id,
        });
      } catch (feedError) {
        console.warn('Failed to create feed event (non-critical):', feedError);
      }

      // Always ensure creator is in participants (non-critical for version creation)
      try {
        const currentUser = useUserStore.getState().currentUser;
        const creatorId = currentUser?.id || data.created_by;
        const participants = data.participants || [];
        const creatorIsParticipant = participants.includes(creatorId);
        
        // If creator not in participants, add them and update the plan
        if (!creatorIsParticipant && creatorId) {
          const updatedParticipants = [...participants, creatorId];
          const { error: updateError } = await supabase
            .from('plans')
            .update({ participants: updatedParticipants })
            .eq('id', data.id);
          
          if (!updateError) {
            // Update local data
            data.participants = updatedParticipants;
            
            // Update local store state
            set((state) => ({
              plans: state.plans.map(p => p.id === data.id ? { ...p, participants: updatedParticipants } : p),
            }));
            
            // Create feed events (non-critical)
            try {
              const { default: useFeedStore } = await import('./feedStore');
              const { allUsers } = useUserStore.getState();
              const creatorUser = allUsers.find(u => u.id === creatorId);
              const creatorName = creatorUser ? creatorUser.name : 'Former User';
              
              useFeedStore.getState().addEvent({
                type: 'plan_participant_added',
                title: 'Added to Plan',
                description: `${creatorName} was added to plan "${data.name}"`,
                userId: creatorId,
                initiatorId: creatorId,
                planId: data.id,
              });

              useFeedStore.getState().addEvent({
                type: 'plan_updated',
                title: 'Participant Added',
                description: `${creatorName} was added to plan "${data.name}"`,
                userId: creatorId,
                initiatorId: creatorId,
                planId: data.id,
              });
            } catch (feedError) {
              console.warn('Failed to create participant feed events:', feedError);
            }
          }
        }

        // Create feed events for other initial participants (if any)
        if (data.participants && data.participants.length > 0) {
          try {
            const { default: useFeedStore } = await import('./feedStore');
            const { allUsers } = useUserStore.getState();
            for (const userId of data.participants) {
              if (userId !== creatorId) {
                const participantUser = allUsers.find(u => u.id === userId);
                const participantName = participantUser ? participantUser.name : 'Former User';
                
                // Notification to participant
                useFeedStore.getState().addEvent({
                  type: 'plan_participant_added',
                  title: 'Added to Plan',
                  description: `${currentUser?.name || 'User'} added you to plan "${data.name}"`,
                  userId: userId,
                  initiatorId: creatorId,
                  planId: data.id,
                });

                // History event
                useFeedStore.getState().addEvent({
                  type: 'plan_updated',
                  title: 'Participant Added',
                  description: `${currentUser?.name || 'User'} added ${participantName} to plan "${data.name}"`,
                  userId: creatorId,
                  initiatorId: creatorId,
                  planId: data.id,
                });
              }
            }
          } catch (feedError) {
            console.warn('Failed to create initial participant feed events:', feedError);
          }
        }
      } catch (participantError) {
        console.warn('Failed to update participants (non-critical):', participantError);
      }
      
      // Create initial version snapshot for new plan
      if (data.id) {
        try {
          const currentUser = useUserStore.getState().currentUser;
          const changeSummary = {
            text: 'Plan created',
            changes: [],
            summary: 'Plan created',
            addedCount: 0,
            removedCount: 0,
            modifiedCount: 0
          };
          
          // Get plan items (empty for new plan)
          const { data: planItems } = await supabase
            .from('plan_items')
            .select('*')
            .eq('plan_id', data.id);
          
          // Create initial snapshot
          await supabase.rpc('create_plan_version_snapshot', {
            p_plan_id: data.id,
            p_version_number: data.version || '0.0.1',
            p_created_by: currentUser?.id || data.created_by,
            p_change_summary: changeSummary,
            p_plan_items: planItems || [],
            p_body_markdown: data.body_markdown ?? ''
          });
        } catch (snapshotError) {
          console.error('Error creating initial version snapshot:', snapshotError);
          // Don't fail plan creation if snapshot fails
        }
      }
      
      set((state) => ({
        plans: [data, ...state.plans],
        isLoading: false
      }));

      return data;
    } catch (error) {
      console.error('Error creating plan:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to create plan', isLoading: false });
      throw error;
    }
  },

  // Update a plan
  updatePlan: async (planId: string, updates: Partial<Plan>, createVersionSnapshot: boolean = false) => {
    set({ isLoading: true, error: null });
    
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('User not authenticated');

      // If creating version snapshot, get current plan state first
      // Also get it if we need to detect participant changes
      let oldPlan: Plan | null = null;
      if (createVersionSnapshot || updates.participants) {
        const { data: currentPlan } = await supabase
          .from('plans')
          .select('*')
          .eq('id', planId)
          .single();
        oldPlan = currentPlan;
      }

      // Clear edit lock on save
      const updatesWithLock = {
        ...updates,
        editing_by: null,
        editing_since: null,
      };

      const { data, error } = await supabase
        .from('plans')
        .update(updatesWithLock)
        .eq('id', planId)
        .select()
        .single();

      if (error) throw error;

      // Detect newly added participants and create feed events
      if (updates.participants && oldPlan) {
        const oldParticipants = oldPlan.participants || [];
        const newParticipants = updates.participants || [];
        const addedParticipants = newParticipants.filter(id => !oldParticipants.includes(id));

        if (addedParticipants.length > 0) {
          const { default: useFeedStore } = await import('./feedStore');
          const { allUsers } = useUserStore.getState();
          
          for (const userId of addedParticipants) {
            const addedUser = allUsers.find(u => u.id === userId);
            const addedUserName = addedUser ? addedUser.name : 'Former User';
            
            // Create feed event for the added participant (notification)
            useFeedStore.getState().addEvent({
              type: 'plan_participant_added',
              title: 'Added to Plan',
              description: `${currentUser.name} added you to plan "${data.name}"`,
              userId: userId, // Recipient of the notification
              initiatorId: currentUser.id,
              planId: planId,
            });

            // Also create a feed event for history (visible to all plan participants)
            useFeedStore.getState().addEvent({
              type: 'plan_updated',
              title: 'Participant Added',
              description: `${currentUser.name} added ${addedUserName} to plan "${data.name}"`,
              userId: currentUser.id,
              initiatorId: currentUser.id,
              planId: planId,
            });
          }
        }
      }

      // Create version snapshot if requested
      if (createVersionSnapshot && oldPlan) {
        console.log('📋 updatePlan: createVersionSnapshot=true, creating snapshot...');
        
        const diff = diffMarkdown(oldPlan.body_markdown ?? '', updates.body_markdown ?? oldPlan.body_markdown ?? '');
        const changeSummary = {
          text: diff.summary,
          summary: diff.summary,
          changes: diff.ops.filter((o) => o.type !== 'equal' && o.text.trim()).map((o) => ({ type: o.type, content: o.text })),
          addedCount: diff.addedCount,
          removedCount: diff.removedCount,
          modifiedCount: 0,
        };
        console.log('  Diff calculated:', changeSummary.summary);
        await get().createVersionSnapshot(planId, changeSummary);
      } else {
        console.log('📋 updatePlan: createVersionSnapshot=false, skipping snapshot');
      }

      set((state) => ({
        plans: state.plans.map(plan => plan.id === planId ? data : plan),
        selectedPlan: state.selectedPlan?.id === planId ? data : state.selectedPlan,
        isLoading: false
      }));
    } catch (error) {
      console.error('Error updating plan:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to update plan', isLoading: false });
    }
  },

  // Delete a plan
  deletePlan: async (planId: string) => {
    set({ isLoading: true, error: null });
    
    try {
      const { error } = await supabase
        .from('plans')
        .delete()
        .eq('id', planId);

      if (error) throw error;

      set((state) => ({
        plans: state.plans.filter(plan => plan.id !== planId),
        selectedPlan: state.selectedPlan?.id === planId ? null : state.selectedPlan,
        isLoading: false
      }));
    } catch (error) {
      console.error('Error deleting plan:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to delete plan', isLoading: false });
    }
  },

  // Create a plan item
  createPlanItem: async (itemData: Omit<PlanItem, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('plan_items')
        .insert([itemData])
        .select()
        .single();

      if (error) throw error;

      set((state) => ({
        selectedPlanItems: [...state.selectedPlanItems, data]
      }));

      return data;
    } catch (error) {
      console.error('Error creating plan item:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to create plan item' });
      throw error;
    }
  },

  // Update a plan item
  updatePlanItem: async (itemId: string, updates: Partial<PlanItem>) => {
    try {
      const { data, error } = await supabase
        .from('plan_items')
        .update(updates)
        .eq('id', itemId)
        .select()
        .single();

      if (error) throw error;

      set((state) => ({
        selectedPlanItems: state.selectedPlanItems.map(item => 
          item.id === itemId ? data : item
        )
      }));
    } catch (error) {
      console.error('Error updating plan item:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to update plan item' });
    }
  },

  // Delete a plan item
  deletePlanItem: async (itemId: string) => {
    try {
      const { error } = await supabase
        .from('plan_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;

      set((state) => ({
        selectedPlanItems: state.selectedPlanItems.filter(item => item.id !== itemId)
      }));
    } catch (error) {
      console.error('Error deleting plan item:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to delete plan item' });
    }
  },

  // Execute a plan (the magic "Execute Plan" button)
  executePlan: async (planId: string, executionType: 'full' | 'partial' | 'incremental') => {
    set({ isLoading: true, error: null });
    
    try {
      const { data, error } = await supabase
        .from('plan_executions')
        .insert([{
          plan_id: planId,
          executed_by: useUserStore.getState().currentUser?.id,
          execution_type: executionType,
          execution_status: 'in_progress'
        }])
        .select()
        .single();

      if (error) throw error;

      // TODO: Implement the actual execution logic here
      // This would involve:
      // 1. Parsing plan items
      // 2. Creating corresponding Currents objects (cards, sprints, requirements)
      // 3. Updating the execution record with created objects
      // 4. Updating plan progress

      set((state) => ({
        planExecutions: [data, ...state.planExecutions],
        isLoading: false
      }));

      return data;
    } catch (error) {
      console.error('Error executing plan:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to execute plan', isLoading: false });
      throw error;
    }
  },

  // Duplicate a plan
  duplicatePlan: async (planId: string, newName: string) => {
    set({ isLoading: true, error: null });
    
    try {
      // First, fetch the original plan
      const { data: originalPlan, error: fetchError } = await supabase
        .from('plans')
        .select('*')
        .eq('id', planId)
        .single();

      if (fetchError) throw fetchError;

      // Create the duplicate
      const duplicateData = {
        ...originalPlan,
        name: newName,
        status: 'draft' as const,
        execution_status: 'not_executed' as const,
        progress_percentage: 0,
        version: '1.0.0'
      };

      delete duplicateData.id;
      delete duplicateData.created_at;
      delete duplicateData.updated_at;

      const { data, error } = await supabase
        .from('plans')
        .insert([duplicateData])
        .select()
        .single();

      if (error) throw error;

      set((state) => ({
        plans: [data, ...state.plans],
        isLoading: false
      }));

      return data;
    } catch (error) {
      console.error('Error duplicating plan:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to duplicate plan', isLoading: false });
      throw error;
    }
  },

  // Create a plan from a template
  createPlanFromTemplate: async (templateId: string, planName: string, teamId?: string) => {
    set({ isLoading: true, error: null });
    
    try {
      const { data: template, error: templateError } = await supabase
        .from('plan_templates')
        .select('*')
        .eq('id', templateId)
        .single();

      if (templateError) throw templateError;

      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('User not authenticated');

      const planData = {
        name: planName,
        description: template.description,
        status: 'draft' as const,
        version: '1.0.0',
        team_id: teamId,
        company_id: currentUser.companyId,
        created_by: currentUser.id,
        participants: [currentUser.id],
        execution_status: 'not_executed' as const,
        progress_percentage: 0,
        // Templates authored after the markdown move store a markdown string; older JSON templates start blank.
        body_markdown: typeof template.template_content === 'string' ? template.template_content : '',
        plan_settings: {},
        tags: template.tags,
        is_template: false,
        priority: 'medium' as const
      };

      const { data, error } = await supabase
        .from('plans')
        .insert([planData])
        .select()
        .single();

      if (error) throw error;

      // Update template usage count
      await supabase
        .from('plan_templates')
        .update({ usage_count: template.usage_count + 1 })
        .eq('id', templateId);

      set((state) => ({
        plans: [data, ...state.plans],
        isLoading: false
      }));

      return data;
    } catch (error) {
      console.error('Error creating plan from template:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to create plan from template', isLoading: false });
      throw error;
    }
  },

  // Fetch plan versions (history)
  fetchPlanVersions: async (planId: string) => {
    try {
      const { data, error } = await supabase
        .from('plan_version_snapshots')
        .select('*')
        .eq('plan_id', planId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      console.log(`  📚 Fetched ${data?.length || 0} version snapshots for plan ${planId}`);
      set({ planVersions: data || [] });
    } catch (error) {
      console.error('Error fetching plan version snapshots:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to fetch plan versions' });
    }
  },

  // Edit locking functions
  acquireEditLock: async (planId: string) => {
    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser) return false;

    try {
      // Check if already locked by another user
      const { data: plan } = await supabase
        .from('plans')
        .select('editing_by, editing_since')
        .eq('id', planId)
        .single();

      if (plan?.editing_by && plan.editing_by !== currentUser.id) {
        // Check if lock is stale (older than 5 minutes)
        if (plan.editing_since) {
          const lockTime = new Date(plan.editing_since).getTime();
          const now = Date.now();
          const fiveMinutes = 5 * 60 * 1000;

          if (now - lockTime < fiveMinutes) {
            return false; // Lock is still valid
          }
        }
      }

      // Acquire lock
      const { error } = await supabase
        .from('plans')
        .update({
          editing_by: currentUser.id,
          editing_since: new Date().toISOString()
        })
        .eq('id', planId);

      if (error) throw error;

      // Update local state
      const { data: updatedPlan } = await supabase
        .from('plans')
        .select('*')
        .eq('id', planId)
        .single();

      if (updatedPlan) {
        set((state) => ({
          plans: state.plans.map(p => p.id === planId ? updatedPlan : p),
          selectedPlan: state.selectedPlan?.id === planId ? updatedPlan : state.selectedPlan,
        }));
      }

      return true;
    } catch (error) {
      console.error('Error acquiring edit lock:', error);
      return false;
    }
  },

  releaseEditLock: async (planId: string) => {
    try {
      const { error } = await supabase
        .from('plans')
        .update({
          editing_by: null,
          editing_since: null
        })
        .eq('id', planId);

      if (error) throw error;

      // Update local state
      const { data: updatedPlan } = await supabase
        .from('plans')
        .select('*')
        .eq('id', planId)
        .single();

      if (updatedPlan) {
        set((state) => ({
          plans: state.plans.map(p => p.id === planId ? updatedPlan : p),
          selectedPlan: state.selectedPlan?.id === planId ? updatedPlan : state.selectedPlan,
        }));
      }
    } catch (error) {
      console.error('Error releasing edit lock:', error);
    }
  },

  checkEditLock: async (planId: string) => {
    try {
      const { data, error } = await supabase
        .from('plans')
        .select('editing_by, editing_since')
        .eq('id', planId)
        .single();

      if (error) throw error;

      const locked = !!data?.editing_by;
      return {
        locked,
        editingBy: data?.editing_by || undefined,
        editingSince: data?.editing_since || undefined
      };
    } catch (error) {
      console.error('Error checking edit lock:', error);
      return { locked: false };
    }
  },

  // Presence tracking functions
  addViewer: async (planId: string) => {
    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser) return;

    try {
      // Try upsert first
      const { error: upsertError } = await supabase
        .from('plan_viewers')
        .upsert({
          plan_id: planId,
          user_id: currentUser.id,
          viewing_since: new Date().toISOString()
        }, {
          onConflict: 'plan_id,user_id'
        });

      // If upsert fails with RLS error, try insert then update as fallback
      if (upsertError && upsertError.code === '42501') {
        // Try insert first
        const { error: insertError } = await supabase
          .from('plan_viewers')
          .insert({
            plan_id: planId,
            user_id: currentUser.id,
            viewing_since: new Date().toISOString()
          });

        // If insert fails (row exists), try update
        if (insertError && insertError.code === '23505') {
          const { error: updateError } = await supabase
            .from('plan_viewers')
            .update({ viewing_since: new Date().toISOString() })
            .eq('plan_id', planId)
            .eq('user_id', currentUser.id);

          if (updateError) throw updateError;
        } else if (insertError) {
          throw insertError;
        }
      } else if (upsertError) {
        throw upsertError;
      }

      await get().fetchViewers(planId);
    } catch (error) {
      // Only log if it's not a permission error (might be expected in some cases)
      if (error && typeof error === 'object' && 'code' in error && error.code !== '42501') {
        console.error('Error adding viewer:', error);
      }
    }
  },

  removeViewer: async (planId: string) => {
    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser) return;

    try {
      const { error } = await supabase
        .from('plan_viewers')
        .delete()
        .eq('plan_id', planId)
        .eq('user_id', currentUser.id);

      if (error) throw error;

      await get().fetchViewers(planId);
    } catch (error) {
      console.error('Error removing viewer:', error);
    }
  },

  fetchViewers: async (planId: string) => {
    try {
      const { data, error } = await supabase
        .from('plan_viewers')
        .select('*')
        .eq('plan_id', planId);

      if (error) throw error;

      set({ planViewers: data || [] });
    } catch (error) {
      console.error('Error fetching viewers:', error);
    }
  },

  setupPresenceSubscription: async (planId: string) => {
    get().cleanupPresenceSubscription();

    const channel = supabase
      .channel(`plan_presence_${planId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'plan_viewers',
          filter: `plan_id=eq.${planId}`,
        },
        () => {
          // Refresh viewers when changes occur
          get().fetchViewers(planId);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'plans',
          filter: `id=eq.${planId}`,
        },
        (payload) => {
          // Update lock status when editing_by changes
          const updatedPlan = payload.new as Plan;
          set((state) => ({
            plans: state.plans.map(p => p.id === planId ? updatedPlan : p),
            selectedPlan: state.selectedPlan?.id === planId ? updatedPlan : state.selectedPlan,
          }));
        }
      )
      .subscribe();

    set({ presenceChannel: channel });
  },

  cleanupPresenceSubscription: () => {
    const { presenceChannel } = get();
    if (presenceChannel) {
      supabase.removeChannel(presenceChannel);
      set({ presenceChannel: null });
    }
  },

  // Version snapshot creation
  createVersionSnapshot: async (planId: string, changeSummary: any) => {
    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser) {
      console.warn('createVersionSnapshot: No current user, skipping snapshot');
      return;
    }

    console.log('📸 Creating version snapshot for plan:', planId);
    console.log('  Change summary:', changeSummary);

    try {
      // Get current plan state
      const { data: plan, error: planError } = await supabase
        .from('plans')
        .select('*')
        .eq('id', planId)
        .single();

      if (planError) {
        console.error('  ❌ Error fetching plan:', planError);
        throw planError;
      }

      console.log('  ✅ Plan fetched:', plan.version);

      // Get current plan items
      const { data: planItems, error: itemsError } = await supabase
        .from('plan_items')
        .select('*')
        .eq('plan_id', planId);

      if (itemsError) {
        console.error('  ❌ Error fetching plan items:', itemsError);
        throw itemsError;
      }

      console.log('  ✅ Plan items fetched:', planItems?.length || 0);

      // Call database function to create snapshot
      console.log('  🔄 Calling create_plan_version_snapshot RPC...');
      const rpcParams = {
        p_plan_id: planId,
        p_version_number: plan.version,
        p_created_by: currentUser.id,
        p_change_summary: changeSummary,
        p_plan_items: planItems || [],
        p_body_markdown: plan.body_markdown ?? ''
      };
      console.log('  📋 RPC Parameters:', {
        p_plan_id: planId,
        p_version_number: plan.version,
        p_created_by: currentUser.id,
        p_change_summary_keys: Object.keys(changeSummary),
        p_body_markdown_length: (plan.body_markdown ?? '').length,
        p_plan_items_count: (planItems || []).length
      });
      const { data: snapshotData, error: snapshotError } = await supabase.rpc('create_plan_version_snapshot', rpcParams);

      if (snapshotError) {
        console.error('  ❌ Snapshot RPC error:', snapshotError);
        console.error('  ❌ Error details:', JSON.stringify(snapshotError, null, 2));
        console.error('  ❌ Error message:', snapshotError.message);
        console.error('  ❌ Error code:', snapshotError.code);
        throw snapshotError;
      }

      console.log('  ✅ Snapshot created:', snapshotData);

      // Refresh versions list
      console.log('  🔄 Refreshing versions list...');
      await get().fetchPlanVersions(planId);
      console.log('  ✅ Versions refreshed. Total versions:', get().planVersions.length);
    } catch (error) {
      console.error('❌ Error creating version snapshot:', error);
      throw error; // Re-throw so caller knows it failed
    }
  },

  // ============================================
  // NEW: Living Plans Versioning Implementations
  // ============================================
  
  /**
   * Fetches every version in a plan family: the root row plus all rows whose
   * parent_version_id is the root. Keyed on the root id (not ticket/project) so
   * project-less plans version the same way as project plans.
   */
  fetchPlanVersionHierarchy: async (rootPlanId: string) => {
    try {
      const { data, error } = await supabase
        .from('plans')
        .select('id, ticket_number, name, major_version, minor_version, patch_version, version, version_state, parent_version_id, executed_at, completed_at, project_id, company_id, created_by, created_at, updated_at')
        .or(`id.eq.${rootPlanId},parent_version_id.eq.${rootPlanId}`)
        .order('major_version', { ascending: true })
        .order('minor_version', { ascending: true })
        .order('patch_version', { ascending: true });

      if (error) throw error;

      return (data || []) as unknown as PlanVersionHierarchy[];
    } catch (error) {
      console.error('Error fetching plan version hierarchy:', error);
      set({ error: 'Failed to load version history' });
      return [];
    }
  },

  /**
   * Creates the next version of a plan (dot or major release).
   * Calls the create_next_plan_version() database function.
   */
  createNextPlanVersion: async (sourcePlanId: string, versionType: 'dot' | 'major') => {
    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    try {
      set({ isLoading: true, error: null });

      // Call database function to create next version
      const { data, error } = await supabase.rpc('create_next_plan_version', {
        p_source_plan_id: sourcePlanId,
        p_version_type: versionType,
        p_user_id: currentUser.id
      });

      if (error) throw error;

      // Fetch the newly created plan
      const { data: newPlan, error: fetchError } = await supabase
        .from('plans')
        .select('*')
        .eq('id', data)
        .single();

      if (fetchError) throw fetchError;

      // Add to plans list
      set((state) => ({
        plans: [newPlan, ...state.plans],
        isLoading: false
      }));

      return newPlan as Plan;
    } catch (error) {
      console.error('Error creating next plan version:', error);
      set({ error: 'Failed to create new version', isLoading: false });
      throw error;
    }
  },

  /**
   * Executes a draft plan (transitions 0.0.X → 1.0.0).
   * Updates version_state to 'executing' and creates work items.
   */
  executeActivePlan: async (planId: string) => {
    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    try {
      set({ isLoading: true, error: null });

      // Update plan to executing state (database trigger handles version bump)
      const { error: updateError } = await supabase
        .from('plans')
        .update({
          version_state: 'executing',
          status: 'active',
          execution_status: 'partial'
        })
        .eq('id', planId);

      if (updateError) throw updateError;

      // Create execution record
      const { data: execution, error: executionError } = await supabase
        .from('plan_executions')
        .insert({
          plan_id: planId,
          executed_by: currentUser.id,
          execution_type: 'full',
          execution_status: 'in_progress',
          execution_summary: {},
          created_cards: [],
          created_sprints: [],
          created_requirements: [],
          created_roadmaps: [],
          execution_log: []
        })
        .select()
        .single();

      if (executionError) throw executionError;

      // Refresh plan to get updated version
      await get().fetchPlan(planId);

      set({ isLoading: false });
      return execution as PlanExecution;
    } catch (error) {
      console.error('Error executing plan:', error);
      set({ error: 'Failed to execute plan', isLoading: false });
      throw error;
    }
  },

  /**
   * Marks an executing plan as complete.
   * Updates version_state to 'complete' (locks the version permanently).
   */
  completePlan: async (planId: string) => {
    try {
      set({ isLoading: true, error: null });

      // Update plan to complete state
      const { error: updateError } = await supabase
        .from('plans')
        .update({
          version_state: 'complete',
          status: 'completed',
          execution_status: 'completed',
          progress_percentage: 100
        })
        .eq('id', planId);

      if (updateError) throw updateError;

      // Refresh plan to get updated state
      await get().fetchPlan(planId);

      set({ isLoading: false });
    } catch (error) {
      console.error('Error completing plan:', error);
      set({ error: 'Failed to complete plan', isLoading: false });
      throw error;
    }
  },

  /**
   * Detects tasks added during plan execution (drift candidates).
   * Calls the detect_plan_drift() database function.
   */
  detectPlanDrift: async (planId: string) => {
    try {
      const { data, error } = await supabase.rpc('detect_plan_drift', {
        p_plan_id: planId
      });

      if (error) throw error;

      return (data || []) as DriftCandidate[];
    } catch (error) {
      console.error('Error detecting plan drift:', error);
      set({ error: 'Failed to detect drift' });
      return [];
    }
  },

  /**
   * Categorizes a drift item (task added during execution).
   * Updates the card's drift metadata for tracking and analytics.
   */
  categorizeDrift: async (cardId: string, driftType: DriftType, reason: string) => {
    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    try {
      // Get the card to find which plan it's drifting from
      const { data: card, error: cardError } = await supabase
        .from('cards')
        .select('project_id')
        .eq('id', cardId)
        .single();

      if (cardError) throw cardError;

      // Find the executing plan for this project (if any)
      const { data: plans, error: plansError } = await supabase
        .from('plans')
        .select('id')
        .eq('project_id', card.project_id)
        .eq('version_state', 'executing')
        .limit(1);

      if (plansError) throw plansError;

      const executingPlanId = plans && plans.length > 0 ? plans[0].id : null;

      // Update card with drift metadata
      const { error: updateError } = await supabase
        .from('cards')
        .update({
          is_drift: true,
          drift_type: driftType,
          drift_reason: reason,
          drift_added_by: currentUser.id,
          drift_added_at: new Date().toISOString(),
          drift_plan_version: executingPlanId,
          drift_categorized_at: new Date().toISOString()
        })
        .eq('id', cardId);

      if (updateError) throw updateError;

      console.log(`Drift categorized: ${driftType} - ${reason}`);
    } catch (error) {
      console.error('Error categorizing drift:', error);
      set({ error: 'Failed to categorize drift' });
      throw error;
    }
  },

  /**
   * Loads a plan version snapshot from the plan_version_snapshots table.
   * Returns the plan_content and plan_items JSON data to restore a previous version.
   */
  loadPlanVersionSnapshot: async (planId: string) => {
    try {
      const { data, error} = await supabase
        .from('plan_version_snapshots')
        .select('body_markdown, plan_items, version_number, created_at')
        .eq('plan_id', planId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error loading plan version snapshot:', error);
      return null;
    }
  },

  // Setters
  setSelectedPlan: (plan: Plan | null) => set({ selectedPlan: plan }),
  setSearchTerm: (term: string) => set({ searchTerm: term }),
  setStatusFilter: (status: string) => set({ statusFilter: status }),
  setTeamFilter: (teamId: string | null) => set({ teamFilter: teamId }),
  clearError: () => set({ error: null }),
}));

export default usePlanStore;
